import React from "react";
import { Link } from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { FaArrowRight, FaEnvelope, FaInstagram, FaLinkedin } from "react-icons/fa";
import LanguageSwitch from "../components/LanguageSwitch";
import { useLanguage } from "../contexts/LanguageContext";
import logo from "../assets/ourdo-tail-curve-wordmark.svg";
import "../styles.css";
import "../homepage-v2.css";
import "../work-carousel.css";

type LocalizedText = { id: string; en: string };

const copy = {
  nav: {
    services: { id: "Layanan", en: "What we do" },
    work: { id: "Portofolio", en: "Work" },
    baruna: { id: "Baruna", en: "Baruna" },
    training: { id: "Pelatihan", en: "Training" },
    about: { id: "Tentang", en: "About" },
  },
  heroEyebrow: {
    id: "Technology · Data · Spatial",
    en: "Technology · Data · Spatial",
  },
  heroTitle: {
    id: "Sistem digital dan data untuk pekerjaan yang kompleks.",
    en: "Digital and data systems for complex work.",
  },
  heroBody: {
    id: "Kami membantu organisasi dan tim proyek membangun produk digital, analisis spasial, dan sistem data yang lebih mudah dipakai.",
    en: "We help organizations and project teams build digital products, spatial analysis, and data systems that are easier to use.",
  },
};

const offers = [
  {
    title: { id: "Digital Product Sprint", en: "Digital Product Sprint" },
    problem: {
      id: "Workflow masih manual atau terfragmentasi.",
      en: "A workflow is still manual or fragmented.",
    },
    outcome: {
      id: "Dari masalah ke prototype dan produk yang bisa diuji.",
      en: "Move from the problem to a prototype and testable product.",
    },
  },
  {
    title: { id: "GIS / Project Area Screening", en: "GIS / Project Area Screening" },
    problem: {
      id: "Tim perlu memahami area proyek dengan cepat.",
      en: "The team needs to understand a project area quickly.",
    },
    outcome: {
      id: "Ubah boundary menjadi evidence spasial yang lebih mudah direview.",
      en: "Turn a project boundary into spatial evidence that is easier to review.",
    },
  },
  {
    title: { id: "Custom Data & Monitoring Systems", en: "Custom Data & Monitoring Systems" },
    problem: {
      id: "Monitoring dan reporting tersebar di banyak tempat.",
      en: "Monitoring and reporting are spread across too many places.",
    },
    outcome: {
      id: "Satukan data, review, dashboard, dan reporting dalam satu workflow.",
      en: "Bring data, review, dashboards, and reporting into one workflow.",
    },
  },
];

const startingPoints = [
  {
    label: { id: "WORKFLOW", en: "WORKFLOW" },
    title: { id: "Pekerjaan masih manual atau terpecah.", en: "The work is still manual or fragmented." },
    body: {
      id: "Temukan bottleneck, lalu tentukan apakah perlu produk digital.",
      en: "Find the bottleneck, then decide whether a digital product is needed.",
    },
    route: "Digital Product Sprint",
  },
  {
    label: { id: "AREA PROYEK", en: "PROJECT AREA" },
    title: { id: "Bukti di sekitar suatu area sulit dirangkai.", en: "The evidence around an area is difficult to assemble." },
    body: {
      id: "Mulai dari boundary dan pertanyaan review.",
      en: "Start with the boundary and the review question.",
    },
    route: "GIS / Project Area Screening",
  },
  {
    label: { id: "MONITORING & REPORTING", en: "MONITORING & REPORTING" },
    title: { id: "Data penting hidup di terlalu banyak tempat.", en: "Important project data lives in too many places." },
    body: {
      id: "Mulai dari alur data sebelum menentukan bentuk sistem.",
      en: "Start with the data workflow before choosing the system shape.",
    },
    route: "Custom Data & Monitoring Systems",
  },
];

const whyItems = [
  {
    title: { id: "Mulai dari masalah operasional", en: "Start from the operational problem" },
    body: {
      id: "Pahami bottleneck dan outcome sebelum memilih implementasi.",
      en: "Understand the bottleneck and outcome before choosing the implementation.",
    },
  },
  {
    title: { id: "Gabungkan product, data, dan spatial bila memang perlu", en: "Connect product, data, and spatial work when needed" },
    body: {
      id: "Gunakan software, data, atau spatial hanya ketika masalah membutuhkannya.",
      en: "Use software, data, or spatial work only when the problem requires it.",
    },
  },
  {
    title: { id: "Hasil harus usable dan mudah direview", en: "Make the output reviewable and usable" },
    body: {
      id: "Output harus jelas, usable, dan mudah direview.",
      en: "The output should be clear, usable, and easy to review.",
    },
  },
];


const selectedWork = [
  {
    name: "BARUNA",
    status: "LIVE PRODUCT",
    category: "Spatial Intelligence",
    description: {
      id: "BARUNA mengubah boundary proyek menjadi evidence spasial yang lebih mudah direview.",
      en: "BARUNA turns a project boundary into spatial evidence that is easier to review.",
    },
    href: "https://baruna.ourdo.id/",
    image: "/assets/portfolio/baruna-thumb.webp",
  },
  {
    name: "Impact MIS",
    status: "PROPOSAL PROTOTYPE",
    category: "Data & Monitoring Systems",
    description: {
      id: "Konsep MIS untuk menyatukan indikator, progress, evidence, dan reporting.",
      en: "An MIS concept for bringing indicators, progress, evidence, and reporting together.",
    },
    href: "https://impact-mis.ourdo.id/",
    image: "/assets/portfolio/impact-mis-thumb.webp",
  },
  {
    name: "Ourdo Atlas",
    status: "LMS MVP",
    category: "Learning Operations Platform",
    description: {
      id: "Learning operations platform untuk admin, mentor, dan student.",
      en: "A learning operations platform for admins, mentors, and students.",
    },
    href: "https://atlas.ourdo.id/login",
    image: "/assets/portfolio/ourdo-atlas-thumb.webp",
  },
  {
    name: "Our Mist Studio",
    status: "LIVE PRODUCT INITIATIVE",
    category: "Game Studio",
    description: {
      id: "Studio game misteri interaktif yang mengubah percakapan, file, foto, dan jejak digital menjadi pengalaman investigasi yang benar-benar dimainkan di web.",
      en: "An interactive mystery game studio creating playable web investigations from conversations, files, photos, and digital traces.",
    },
    href: "https://mist.ourdo.id/",
    image: "/assets/portfolio/our-mist-thumb.webp",
  },
  {
    name: "Our Mercury",
    status: "PRODUCT PROTOTYPE",
    category: "Travel Guide Marketplace",
    description: {
      id: "Platform marketplace untuk mempertemukan traveler dengan local guide dan tour guide yang membantu traveler menemukan orang lokal yang relevan, membandingkan profile, lalu mengirim request perjalanan dengan lebih percaya diri.",
      en: "A marketplace platform connecting travelers with local and tour guides through discovery, profile comparison, and trip-request flows.",
    },
    href: "https://our-mercury-web.vercel.app/",
    image: "/assets/portfolio/our-mercury-thumb.webp",
  },
  {
    name: "Lunarian",
    status: "COMMERCE PROTOTYPE",
    category: "Marketplace Example",
    description: {
      id: "Contoh marketplace commerce yang menunjukkan bagaimana katalog, product discovery, checkout, dan membership bisa dibentuk menjadi pengalaman belanja yang terstruktur.",
      en: "A marketplace example showing how catalog browsing, product discovery, checkout, and membership can be shaped into a structured commerce experience.",
    },
    href: "https://lunarian-ten.vercel.app/",
    image: "/assets/portfolio/lunarian-thumb.webp",
  },
];

const LandingPage: React.FC = () => {
  const { language } = useLanguage();
  const isEnglish = language === "en";
  const t = (text: LocalizedText) => (isEnglish ? text.en : text.id);

  const contactEmail = "mailto:official@ourdo.id?subject=Project%20inquiry%20for%20Ourdo%20Indonesia";
  const instagramUrl = "https://www.instagram.com/ourdo.id/";
  const linkedInUrl = "https://www.linkedin.com/company/ourdo-indonesia/";

  return (
    <div className="ourdo-site">
      <header className="ourdo-header">
        <div className="ourdo-container ourdo-nav">
          <a href="#top" className="ourdo-brand" aria-label="Ourdo Indonesia home">
            <img src={logo} alt="OURDO Indonesia — Tail Curve" />
            <div>
              <strong>INDONESIA</strong>
              <span>{isEnglish ? "Digital products · data · spatial intelligence" : "Produk digital · data · spatial intelligence"}</span>
            </div>
          </a>

          <nav className="ourdo-nav-links" aria-label="Primary navigation">
            <a href="#services">{t(copy.nav.services)}</a>
            <a href="#work">{t(copy.nav.work)}</a>
            <a href="#baruna">{t(copy.nav.baruna)}</a>
            <Link to="/learning">{t(copy.nav.training)}</Link>
            <a href="#about">{t(copy.nav.about)}</a>
          </nav>

          <div className="ourdo-nav-actions">
            <LanguageSwitch />
            <a className="ourdo-btn ourdo-btn-primary ourdo-nav-cta" href={contactEmail}>
              {isEnglish ? "Discuss a project" : "Diskusikan proyek"}
            </a>
          </div>
        </div>
      </header>

      <main id="top">
        <section className="ourdo-container ourdo-hero">
          <div className="ourdo-hero-copy">
            <p className="ourdo-eyebrow">{t(copy.heroEyebrow)}</p>
            <h1>{t(copy.heroTitle)}</h1>
            <p className="ourdo-lead">{t(copy.heroBody)}</p>
            <div className="ourdo-actions">
              <a className="ourdo-btn ourdo-btn-primary" href={contactEmail}>
                {isEnglish ? "Discuss a project" : "Diskusikan proyek"} <FaArrowRight />
              </a>
              <Link className="ourdo-btn ourdo-btn-secondary" to="/work">
                {isEnglish ? "View all work" : "Lihat semua work"}
              </Link>
            </div>

            <div className="ourdo-focus-strip">
              <div>
                <strong>{isEnglish ? "Build a product" : "Bangun produk"}</strong>
                <span>{isEnglish ? "Internal tools, workflow systems, platforms, and focused product sprints." : "Internal tools, workflow systems, platform, dan product sprint yang fokus."}</span>
              </div>
              <div>
                <strong>{isEnglish ? "Understand a project area" : "Pahami area proyek"}</strong>
                <span>{isEnglish ? "GIS screening and spatial or environmental evidence around a real boundary." : "GIS screening serta bukti spasial atau lingkungan berdasarkan boundary nyata."}</span>
              </div>
              <div>
                <strong>{isEnglish ? "Improve monitoring & reporting" : "Perbaiki monitoring & reporting"}</strong>
                <span>{isEnglish ? "Operational data, dashboards, and information workflows that are easier to use." : "Data operasional, dashboard, dan workflow informasi yang lebih mudah digunakan."}</span>
              </div>
            </div>
          </div>

          <div className="ourdo-evidence-card" aria-label="Baruna flagship product preview">
            <div className="ourdo-evidence-head">
              <span>FLAGSHIP PROOF · BARUNA</span>
              <span className="ourdo-status">AVAILABLE</span>
            </div>
            <div className="ourdo-map-preview ourdo-map-preview-image">
              <img src="/assets/portfolio/baruna-cover.webp" alt="BARUNA project-area environmental screening interface" />
            </div>
            <div className="ourdo-evidence-foot">
              <div>
                <strong>{isEnglish ? "Project-area evidence workflow" : "Workflow evidence area proyek"}</strong>
                <span>AOI → evidence → interpretation → review-ready output</span>
              </div>
              <span className="ourdo-chip">AOI</span>
            </div>
          </div>
        </section>

        <section className="ourdo-section">
          <div className="ourdo-container">
            <div className="ourdo-section-heading">
              <p className="ourdo-eyebrow">{isEnglish ? "COMMON STARTING POINTS" : "TITIK MULAI YANG UMUM"}</p>
              <h2>{isEnglish ? "Start with the work that is breaking down." : "Mulai dari pekerjaan yang paling menghambat."}</h2>
              <p>{isEnglish ? "Start from a real bottleneck, not a technology list." : "Mulai dari bottleneck nyata, bukan daftar teknologi."}</p>
            </div>
            <div className="ourdo-grid-3">
              {startingPoints.map((item) => (
                <article className="ourdo-start-card" key={item.route}>
                  <span>{t(item.label)}</span>
                  <h3>{t(item.title)}</h3>
                  <p>{t(item.body)}</p>
                  <strong>{isEnglish ? "Relevant offer" : "Layanan terkait"} → {item.route}</strong>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="ourdo-section" id="services">
          <div className="ourdo-container">
            <div className="ourdo-section-heading">
              <p className="ourdo-eyebrow">{isEnglish ? "WHAT YOU CAN BUY" : "YANG BISA ANDA BELI"}</p>
              <h2>{isEnglish ? "Three focused ways to work with Ourdo." : "Tiga cara utama bekerja dengan Ourdo."}</h2>
              <p>{isEnglish ? "Start with the problem and expected outcome. Choose technology after." : "Mulai dari masalah dan outcome. Teknologi dipilih setelahnya."}</p>
            </div>

            <div className="ourdo-grid-3">
              {offers.map((offer) => (
                <article className="ourdo-offer-card" key={offer.title.en}>
                  <p className="ourdo-eyebrow">{isEnglish ? "COMMERCIAL OFFER" : "COMMERCIAL OFFER"}</p>
                  <h3>{t(offer.title)}</h3>
                  <div>
                    <span className="ourdo-mini-label">{isEnglish ? "When this fits" : "Cocok ketika"}</span>
                    <p>{t(offer.problem)}</p>
                  </div>
                  <div className="ourdo-offer-outcome">
                    <span>{isEnglish ? "Expected outcome" : "Outcome yang diharapkan"}</span>
                    <strong>{t(offer.outcome)}</strong>
                  </div>
                  <a href={contactEmail}>
                    {isEnglish ? "Discuss this offer" : "Diskusikan offer ini"} →
                  </a>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="ourdo-section" id="baruna">
          <div className="ourdo-container">
            <div className="ourdo-baruna-block">
              <div>
                <p className="ourdo-eyebrow">FLAGSHIP PROOF · BARUNA</p>
                <h2>{isEnglish ? "From project boundary to reviewable evidence." : "Dari project boundary ke evidence yang bisa ditinjau."}</h2>
                <p>{isEnglish ? "BARUNA shows how Ourdo combines spatial data, workflow, and traceable output for project-area review." : "BARUNA menunjukkan bagaimana Ourdo menggabungkan spatial data, workflow, dan output yang traceable untuk review area proyek."}</p>
                <div className="ourdo-actions">
                  <a className="ourdo-btn ourdo-btn-light" href="https://baruna.ourdo.id/" target="_blank" rel="noreferrer">
                    {isEnglish ? "Explore Baruna" : "Explore Baruna"}
                  </a>
                  <a className="ourdo-btn ourdo-btn-outline-light" href={contactEmail}>
                    {isEnglish ? "Discuss a spatial project" : "Diskusikan proyek spatial"}
                  </a>
                </div>
              </div>
              <div className="ourdo-baruna-facts">
                <div><span>01</span><strong>Boundary-first workflow</strong></div>
                <div><span>02</span><strong>Traceable sources</strong></div>
                <div><span>03</span><strong>Review-ready output</strong></div>
              </div>
            </div>
          </div>
        </section>

        <section className="ourdo-section" id="work">
          <div className="ourdo-container">
            <div className="ourdo-section-heading">
              <p className="ourdo-eyebrow">{isEnglish ? "SELECTED WORK" : "SELECTED WORK"}</p>
              <h2>{isEnglish ? "Selected work that shows how we build." : "Selected work yang menunjukkan cara kami membangun."}</h2>
              <p>{isEnglish ? "A small set of relevant proof, not a project gallery." : "Sedikit proof yang relevan, bukan galeri semua proyek."}</p>
            </div>

            <div className="ourdo-work-carousel" role="region" aria-label="Selected Ourdo work">
              {selectedWork.slice(0, 3).map((item) => (
                <article className="ourdo-work-slide" key={item.name}>
                  {"image" in item && item.image ? (
                    <div className="ourdo-work-slide-media">
                      <img src={item.image} alt={`${item.name} product interface`} loading="lazy" />
                    </div>
                  ) : null}
                  <div className="ourdo-work-meta">
                    <span className="ourdo-status">{item.status}</span>
                    <span>{item.category}</span>
                  </div>
                  <h3>{item.name}</h3>
                  <p>{t(item.description)}</p>
                  <div className="ourdo-work-slide-actions">
                    <a href={item.href} target="_blank" rel="noreferrer">
                      {isEnglish ? "Open project" : "Buka project"} →
                    </a>
                    <Link to="/work">
                      {isEnglish ? "View details" : "Lihat detail"}
                    </Link>
                  </div>
                </article>
              ))}
            </div>

            <div className="ourdo-view-all-row">
              <Link className="ourdo-btn ourdo-btn-secondary" to="/work">
                {isEnglish ? "View all work" : "Lihat semua work"} <FaArrowRight />
              </Link>
            </div>
          </div>
        </section>

        <section className="ourdo-section">
          <div className="ourdo-container ourdo-why">
            <div className="ourdo-section-heading">
              <p className="ourdo-eyebrow">{isEnglish ? "HOW OURDO WORKS" : "CARA OURDO BEKERJA"}</p>
              <h2>{isEnglish ? "Problem first. Build only what matters." : "Problem first. Bangun hanya yang diperlukan."}</h2>
            </div>
            <div className="ourdo-why-list">
              {whyItems.map((item, index) => (
                <article key={item.title.en}>
                  <span>0{index + 1}</span>
                  <div>
                    <h3>{t(item.title)}</h3>
                    <p>{t(item.body)}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="ourdo-section" id="training">
          <div className="ourdo-container">
            <div className="ourdo-section-heading">
              <p className="ourdo-eyebrow">{isEnglish ? "LEARNING & CAPACITY BUILDING" : "LEARNING & CAPACITY BUILDING"}</p>
              <h2>{isEnglish ? "Transfer capability, not only systems." : "Transfer capability, bukan hanya deliver sistem."}</h2>
              <p>{isEnglish ? "Training and capacity building remain part of Ourdo for organizations, teams, and individual learners." : "Training dan capacity building tetap menjadi bagian Ourdo untuk organisasi, tim, dan pembelajar individual."}</p>
            </div>

            <div className="ourdo-learning-stats">
              <article><strong>40+</strong><span>{isEnglish ? "Events delivered" : "Acara terlaksana"}</span></article>
              <article><strong>145+</strong><span>{isEnglish ? "Learners supported" : "Peserta terbimbing"}</span></article>
              <article><strong>96%</strong><span>{isEnglish ? "Published satisfaction rate" : "Tingkat kepuasan yang sudah dipublikasikan"}</span></article>
            </div>

            <div className="ourdo-training-list">
              <article><strong>{isEnglish ? "Classes & mentoring" : "Classes & mentoring"}</strong><span>{isEnglish ? "Backend, Frontend, coding programmes, and focused technical mentoring." : "Backend, Frontend, coding programmes, dan mentoring teknis yang fokus."}</span></article>
              <article><strong>{isEnglish ? "For organizations" : "Untuk organisasi"}</strong><span>{isEnglish ? "Team training, facilitation, and practical capacity building." : "Team training, facilitation, dan capacity building yang praktis."}</span></article>
              <article><strong>{isEnglish ? "Learning experience support" : "Learning experience support"}</strong><span>{isEnglish ? "Learning experience design, digital facilitation, and community upskilling." : "Learning experience design, digital facilitation, dan community upskilling."}</span></article>
            </div>

            <div className="ourdo-view-all-row">
              <Link className="ourdo-btn ourdo-btn-secondary" to="/learning">
                {isEnglish ? "Explore Learning" : "Explore Learning"} <FaArrowRight />
              </Link>
            </div>
          </div>
        </section>

        <section className="ourdo-section" id="about">
          <div className="ourdo-container">
            <div className="ourdo-cta-block">
              <div>
                <h2>{isEnglish ? "What is slowing your team down?" : "Apa yang sedang menghambat tim Anda?"}</h2>
                <p>{isEnglish ? "Tell us the problem. We will help shape the most practical next step." : "Ceritakan masalahnya. Kami bantu menentukan langkah berikutnya yang paling masuk akal."}</p>
              </div>
              <a className="ourdo-btn ourdo-btn-light" href={contactEmail}>
                {isEnglish ? "Discuss a project" : "Diskusikan proyek"} <FaArrowRight />
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="ourdo-footer">
        <div className="ourdo-container ourdo-footer-inner">
          <div className="ourdo-brand">
            <img src={logo} alt="" />
            <div><strong>INDONESIA</strong><span>ourdo.id</span></div>
          </div>
          <div className="ourdo-footer-links">
            <a href="mailto:official@ourdo.id"><FaEnvelope /> official@ourdo.id</a>
            <a href={instagramUrl} target="_blank" rel="noreferrer">
              <FaInstagram /> Instagram
            </a>
            <a href={linkedInUrl} target="_blank" rel="noreferrer">
              <FaLinkedin /> LinkedIn
            </a>
          </div>
        </div>
      </footer>

      <Analytics />
      <SpeedInsights />
    </div>
  );
};

export default LandingPage;
